import { useState, useEffect, useCallback } from 'react';
import { useResearchStore } from '@/stores/research';
import {
  Search,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Building2,
  Globe,
} from 'lucide-react';
import { clsx } from 'clsx';

const depthOptions = [
  { value: 'basic' as const, label: 'Basic', desc: 'Quick profile scan' },
  { value: 'standard' as const, label: 'Standard', desc: 'Full web research' },
  { value: 'deep' as const, label: 'Deep', desc: 'Comprehensive enrichment' },
];

export function ResearchPage() {
  const { jobs, activeJob, loading, error, startResearch, pollStatus, clearError } =
    useResearchStore();
  const [companyName, setCompanyName] = useState('');
  const [domain, setDomain] = useState('');
  const [depth, setDepth] = useState<'basic' | 'standard' | 'deep'>('standard');

  // Poll active job
  useEffect(() => {
    if (!activeJob || activeJob.status === 'completed' || activeJob.status === 'failed') return;
    const id = setInterval(() => pollStatus(activeJob.research_id), 3000);
    return () => clearInterval(id);
  }, [activeJob, pollStatus]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!companyName.trim()) return;
      clearError();
      await startResearch({
        company_name: companyName.trim(),
        domain: domain.trim() || undefined,
        research_depth: depth,
      });
      setCompanyName('');
      setDomain('');
    },
    [companyName, domain, depth, startResearch, clearError],
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-navy-900">Customer Research</h1>
        <p className="text-sm text-slate-500 mt-1">
          Start enrichment workflows for customer profiles. Research runs
          asynchronously through the planning, web research, enrichment, and
          resolution pipeline.
        </p>
      </div>

      {/* Start new research */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-navy-900 text-sm mb-4">
          New Research Job
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Company Name *
              </label>
              <div className="relative">
                <Building2
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Corp"
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Domain (optional)
              </label>
              <div className="relative">
                <Globe
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="e.g. acme.com"
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal"
                />
              </div>
            </div>
          </div>

          {/* Depth selector */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Research Depth
            </label>
            <div className="flex gap-3">
              {depthOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDepth(opt.value)}
                  className={clsx(
                    'flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all',
                    depth === opt.value
                      ? 'bg-brand-teal/10 border-brand-teal text-brand-teal-dark'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300',
                  )}
                >
                  <div>{opt.label}</div>
                  <div className="text-[10px] font-normal text-slate-400 mt-0.5">
                    {opt.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !companyName.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal text-navy-900 font-semibold text-sm rounded-lg hover:bg-brand-teal/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Play size={16} />
            )}
            Start Research
          </button>
        </form>
      </div>

      {/* Jobs list */}
      <div>
        <h2 className="font-semibold text-navy-900 text-sm mb-3">
          Research Jobs
          {jobs.length > 0 && (
            <span className="ml-2 text-xs text-slate-400 font-normal">
              ({jobs.length})
            </span>
          )}
        </h2>

        {jobs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Search size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">
              No research jobs yet. Start one above.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <JobCard key={job.research_id} job={job} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function JobCard({ job }: { job: ReturnType<typeof useResearchStore.getState>['jobs'][number] }) {
  const statusConfig: Record<
    string,
    { icon: React.ElementType; color: string; bg: string }
  > = {
    pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    running: { icon: Loader2, color: 'text-blue-600', bg: 'bg-blue-50' },
    in_progress: { icon: Loader2, color: 'text-blue-600', bg: 'bg-blue-50' },
    completed: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  };

  const cfg = statusConfig[job.status] ?? statusConfig.pending;
  const Icon = cfg.icon;
  const isRunning = job.status === 'running' || job.status === 'in_progress';

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 hover:shadow-soft transition-all">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={clsx(
              'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
              cfg.bg,
              cfg.color,
            )}
          >
            <Icon size={16} className={clsx(isRunning && 'animate-spin')} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy-900 truncate">
              {job.company_name}
            </p>
            <p className="text-[11px] text-slate-400 font-mono">
              {job.research_id}
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0 ml-4">
          <span
            className={clsx(
              'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full',
              cfg.bg,
              cfg.color,
            )}
          >
            {job.status}
          </span>
          {job.created_at && (
            <p className="text-[10px] text-slate-400 mt-1">
              {new Date(job.created_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
